import {
  LEAGUE_MAX_HUMANS,
  LEAGUE_MIN_HUMANS,
  LEAGUE_NAME_MAX_LENGTH,
  LEAGUE_NAME_MIN_LENGTH,
  MARKET_MIN_OFFER_RATIO_FLOOR,
  channelName,
  type LeagueMember,
  type LeagueSettings,
} from "@legacy-manager/shared";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { contagemRegressiva, dataEHora, tempoRelativo, useT, type Chave } from "../i18n";
import { useErroEmToast } from "../ui/avisos";
import {
  ConviteSchema,
  DetalheDeLigaSchema,
  PaginaDeLigasSchema,
  ResumoDeLigaSchema,
  RodadaForcadaSchema,
  type DetalheDeLiga,
  type ResumoDeLiga,
} from "../online/esquemas";
import {
  marcarConvite,
  registrarPush,
  valeConvidar,
  type ResultadoDoRegistro,
} from "../online/push";
import { mensagemDoErro, useRelogio, useSessao } from "../online/sessao";
import { Panel, Stat } from "../ui/atoms";
import { Pagina } from "../ui/Pagina";
import { EstadoDaConexao, SituacaoDeDecisao } from "../ui/online";
import { TituloDeTreinador } from "../ui/cosmeticos";

/**
 * Ligas online: achar uma, entrar nela, abrir a sua, e o saguão antes da bola
 * rolar.
 *
 * A tela é uma só de propósito. Vitrine pública, minhas ligas, código de
 * convite e criação são quatro portas para o mesmo lugar, e separá-las em
 * telas diferentes obrigaria a voltar ao início a cada tentativa frustrada.
 *
 * O saguão é a parte que muda sozinha: ele assina `league:{id}` e reage a
 * entrada, saída, presença e mudança de estado. Nada do que chega pelo socket
 * é a fonte da verdade, porém: quando algo muda, a tela relê a liga pela API,
 * que é o que o protocolo manda fazer (docs/WEBSOCKET.md 3).
 */

const POR_PAGINA = 20;

type Aba = "minhas" | "publicas" | "criar";

export function Ligas({
  aoAbrirLiga,
  aoVoltar,
}: {
  /** Chamado quando a pessoa quer jogar a liga, e não só administrá-la. */
  aoAbrirLiga: (leagueId: string) => void;
  aoVoltar?: () => void;
}): ReactNode {
  const t = useT();
  const [aba, definirAba] = useState<Aba>("minhas");
  const [saguao, definirSaguao] = useState<string | null>(null);

  if (saguao) {
    return (
      <Saguao
        leagueId={saguao}
        aoAbrirLiga={aoAbrirLiga}
        aoVoltar={() => definirSaguao(null)}
      />
    );
  }

  const abas: Array<{ chave: Aba; rotulo: Chave }> = [
    { chave: "minhas", rotulo: "ligas.abas.minhas" },
    { chave: "publicas", rotulo: "ligas.abas.publicas" },
    { chave: "criar", rotulo: "ligas.abas.criar" },
  ];

  return (
    <Pagina titulo={t("ligas.titulo")} aoVoltar={aoVoltar}>
      <div className="row" style={{ gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
        {abas.map((entrada) => (
          <button
            key={entrada.chave}
            className="ghost"
            onClick={() => definirAba(entrada.chave)}
            style={
              aba === entrada.chave
                ? { borderColor: "var(--primary)", color: "var(--primary)", fontWeight: 700 }
                : undefined
            }
          >
            {t(entrada.rotulo)}
          </button>
        ))}
      </div>

      {aba === "minhas" && (
        <>
          <EntrarPorCodigo aoEntrar={(id) => definirSaguao(id)} />
          <ListaDeLigas
            titulo={t("ligas.abas.minhas")}
            caminho="/v1/leagues/mine"
            vazio={t("ligas.semMinhas")}
            aoAbrir={definirSaguao}
          />
        </>
      )}
      {aba === "publicas" && (
        <ListaDeLigas
          titulo={t("ligas.abas.publicas")}
          caminho="/v1/leagues/public"
          vazio={t("ligas.semPublicas")}
          aoAbrir={definirSaguao}
        />
      )}
      {aba === "criar" && <CriarLiga aoCriar={(id) => definirSaguao(id)} />}
    </Pagina>
  );
}

// ---------------------------------------------------------------------------
// Listagens
// ---------------------------------------------------------------------------

function ListaDeLigas({
  titulo,
  caminho,
  vazio,
  aoAbrir,
}: {
  titulo: string;
  caminho: string;
  vazio: string;
  aoAbrir: (leagueId: string) => void;
}): ReactNode {
  const t = useT();
  const sessao = useSessao();
  const [itens, definirItens] = useState<ResumoDeLiga[] | null>(null);
  const [cursor, definirCursor] = useState<string | null>(null);
  const [erro, definirErro] = useState<string | null>(null);
  useErroEmToast(erro);
  const [ocupado, definirOcupado] = useState(false);

  const carregar = useCallback(
    async (apartirDe: string | null) => {
      definirOcupado(true);
      definirErro(null);
      try {
        const busca = new URLSearchParams({ limit: String(POR_PAGINA) });
        if (apartirDe) busca.set("cursor", apartirDe);
        const pagina = await sessao.cliente.pedir(
          { metodo: "GET", caminho: `${caminho}?${busca.toString()}` },
          PaginaDeLigasSchema,
        );
        definirItens((atuais) => (apartirDe ? [...(atuais ?? []), ...pagina.items] : pagina.items));
        definirCursor(pagina.nextCursor);
      } catch (causa) {
        definirErro(mensagemDoErro(causa));
        definirItens((atuais) => atuais ?? []);
      } finally {
        definirOcupado(false);
      }
    },
    [sessao, caminho],
  );

  useEffect(() => {
    void carregar(null);
  }, [carregar]);

  return (
    <Panel
      title={titulo}
      action={
        <button
          className="ghost"
          disabled={ocupado}
          onClick={() => void carregar(null)}
          style={{ minHeight: 26, padding: "2px 8px", fontSize: 11 }}
        >
          {t("ligas.atualizar")}
        </button>
      }
    >
      {itens === null && <p className="muted" style={{ margin: 0 }}>{t("online.carregando")}</p>}
      {itens !== null && itens.length === 0 && (
        <p className="muted" style={{ margin: 0 }}>
          {vazio}
        </p>
      )}

      <div className="grid" style={{ gap: 10 }}>
        {(itens ?? []).map((liga) => (
          <LinhaDeLiga key={liga.id} liga={liga} aoAbrir={() => aoAbrir(liga.id)} />
        ))}
      </div>

      {cursor && (
        <button
          className="ghost"
          disabled={ocupado}
          onClick={() => void carregar(cursor)}
          style={{ marginTop: 10 }}
        >
          {t("ligas.carregarMais")}
        </button>
      )}
    </Panel>
  );
}

function LinhaDeLiga({ liga, aoAbrir }: { liga: ResumoDeLiga; aoAbrir: () => void }): ReactNode {
  const t = useT();
  const agora = useRelogio(liga.deadline !== null);

  return (
    <div
      className="row"
      style={{
        gap: 12,
        padding: "10px 12px",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-sm)",
        background: "var(--surface-2)",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontWeight: 700 }}>{liga.name}</span>
          <span className="label">{t(`ligas.estados.${liga.state}`)}</span>
          {liga.visibility && (
            <span className="label-muted">{t(`ligas.visibilidades.${liga.visibility}`)}</span>
          )}
        </div>
        <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>
          {t("ligas.linha.temporadaRodada", {
            temporada: liga.season ?? 0,
            rodada: liga.round,
          })}
          {liga.maxHumans !== undefined &&
            ` · ${t("ligas.linha.treinadores", {
              humanos: liga.humanCount ?? 0,
              max: liga.maxHumans,
            })}`}
        </div>
        <div className="muted" style={{ fontSize: 11.5 }}>
          {liga.deadline
            ? t("ligas.linha.prazo", {
                quando: contagemRegressiva(Date.parse(liga.deadline) - agora),
              })
            : t("ligas.linha.semPrazo")}
        </div>
      </div>
      <button className="ghost" onClick={aoAbrir}>
        {t("ligas.linha.abrir")}
      </button>
    </div>
  );
}

function EntrarPorCodigo({ aoEntrar }: { aoEntrar: (leagueId: string) => void }): ReactNode {
  const t = useT();
  const sessao = useSessao();
  const [codigo, definirCodigo] = useState("");
  const [erro, definirErro] = useState<string | null>(null);
  useErroEmToast(erro);
  const [ocupado, definirOcupado] = useState(false);

  const entrar = async (): Promise<void> => {
    definirErro(null);
    definirOcupado(true);
    try {
      const liga = await sessao.cliente.pedir(
        { metodo: "POST", caminho: "/v1/leagues/join", corpo: { code: codigo.trim().toUpperCase() } },
        ResumoDeLigaSchema,
      );
      aoEntrar(liga.id);
    } catch (causa) {
      definirErro(mensagemDoErro(causa));
    } finally {
      definirOcupado(false);
    }
  };

  return (
    <Panel title={t("ligas.entrarPorCodigo")}>
      <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
        <input
          value={codigo}
          onChange={(evento) => definirCodigo(evento.target.value.toUpperCase())}
          placeholder={t("ligas.codigo")}
          maxLength={12}
          style={{ flex: 1, minWidth: 160, letterSpacing: "0.12em", fontWeight: 700 }}
        />
        <button
          className="primary"
          disabled={ocupado || codigo.trim().length < 6}
          onClick={() => void entrar()}
        >
          {t("ligas.entrar")}
        </button>
      </div>
      <p className="muted" style={{ margin: "6px 0 0", fontSize: 11 }}>
        {t("ligas.codigoAjuda")}
      </p>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Criar liga
// ---------------------------------------------------------------------------

/**
 * Países que o servidor aceita hoje.
 *
 * A lista não é livre: o gerador do motor precisa de um conjunto de nomes por
 * região, e país sem conjunto sairia com elenco inteiro de outro lugar. Quem
 * quiser mais países publica um datapack.
 */
const PAISES = ["BR", "AR", "ES", "EN", "IT", "PT"] as const;

const DIFICULDADES = ["facil", "normal", "dificil"] as const;

function fusoDoAparelho(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Sao_Paulo";
  } catch {
    return "America/Sao_Paulo";
  }
}

function CriarLiga({ aoCriar }: { aoCriar: (leagueId: string) => void }): ReactNode {
  const t = useT();
  const sessao = useSessao();
  const [erro, definirErro] = useState<string | null>(null);
  useErroEmToast(erro);
  const [ocupado, definirOcupado] = useState(false);

  const [forma, definirForma] = useState<LeagueSettings>(() => ({
    name: "",
    visibility: "private",
    maxHumans: 8,
    datapackId: null,
    countries: ["BR"],
    divisionsPerCountry: 2,
    clubAssignment: "sorteio",
    pace: {
      mode: "async",
      cron: "0 20 * * *",
      timezone: fusoDoAparelho(),
      liveTimeoutMinutes: 10,
    },
    seasons: 5,
    market: {
      humanTrades: true,
      minOfferRatio: 0.7,
      maxTradesPerPair: 2,
      commissionerApproval: false,
    },
    difficulty: "normal",
  }));

  /** Troca um campo solto sem espalhar `setState` por vinte lugares. */
  const mudar = <C extends keyof LeagueSettings>(campo: C, valor: LeagueSettings[C]): void => {
    definirForma((atual) => ({ ...atual, [campo]: valor }));
  };

  const alternarPais = (codigo: string): void => {
    definirForma((atual) => {
      const tem = atual.countries.includes(codigo);
      const proximos = tem
        ? atual.countries.filter((outro) => outro !== codigo)
        : [...atual.countries, codigo];
      return { ...atual, countries: proximos.slice(0, 8) };
    });
  };

  const criar = async (): Promise<void> => {
    definirErro(null);
    if (forma.countries.length === 0) {
      definirErro(t("ligas.criar.escolhaUmPais"));
      return;
    }
    definirOcupado(true);
    try {
      const liga = await sessao.cliente.pedir(
        { metodo: "POST", caminho: "/v1/leagues", corpo: { ...forma, name: forma.name.trim() } },
        DetalheDeLigaSchema,
      );
      aoCriar(liga.id);
    } catch (causa) {
      definirErro(mensagemDoErro(causa));
    } finally {
      definirOcupado(false);
    }
  };

  const aoVivo = forma.pace.mode === "live";

  return (
    <Panel title={t("ligas.criar.titulo")}>
      <div style={{ display: "grid", gap: 14 }}>
        <p className="label-muted" style={{ margin: 0 }}>
          {t("ligas.criar.exigePasse")}
        </p>

        <Campo rotulo={t("ligas.criar.nome")}>
          <input
            value={forma.name}
            onChange={(evento) => mudar("name", evento.target.value)}
            minLength={LEAGUE_NAME_MIN_LENGTH}
            maxLength={LEAGUE_NAME_MAX_LENGTH}
          />
        </Campo>

        <Campo rotulo={t("ligas.criar.visibilidade")}>
          <select
            value={forma.visibility}
            onChange={(evento) =>
              mudar("visibility", evento.target.value as LeagueSettings["visibility"])
            }
          >
            <option value="private">{t("ligas.criar.privada")}</option>
            <option value="public">{t("ligas.criar.publica")}</option>
          </select>
        </Campo>

        <Campo rotulo={t("ligas.criar.maxHumanos")}>
          <input
            type="number"
            value={forma.maxHumans}
            min={LEAGUE_MIN_HUMANS}
            max={LEAGUE_MAX_HUMANS}
            onChange={(evento) => mudar("maxHumans", Number(evento.target.value))}
          />
        </Campo>

        <Campo rotulo={t("ligas.criar.paises")} ajuda={t("ligas.criar.paisesAjuda")}>
          <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
            {PAISES.map((codigo) => {
              const marcado = forma.countries.includes(codigo);
              return (
                <button
                  key={codigo}
                  type="button"
                  className="ghost"
                  onClick={() => alternarPais(codigo)}
                  style={{
                    minHeight: 30,
                    padding: "2px 10px",
                    fontSize: 12,
                    ...(marcado
                      ? { borderColor: "var(--primary)", color: "var(--primary)", fontWeight: 700 }
                      : {}),
                  }}
                >
                  {t(`ligas.criar.paisesNomes.${codigo}`)}
                </button>
              );
            })}
          </div>
        </Campo>

        <Campo rotulo={t("ligas.criar.divisoes")}>
          <select
            value={forma.divisionsPerCountry}
            onChange={(evento) => mudar("divisionsPerCountry", Number(evento.target.value))}
          >
            {[1, 2, 3, 4].map((quantas) => (
              <option key={quantas} value={quantas}>
                {t("ligas.criar.divisoesOpcao", { numero: quantas })}
              </option>
            ))}
          </select>
        </Campo>

        <Campo rotulo={t("ligas.criar.ritmo")} ajuda={aoVivo ? t("ligas.criar.ritmoLiveAjuda") : t("ligas.criar.ritmoAsyncAjuda")}>
          <select
            value={forma.pace.mode}
            onChange={(evento) =>
              mudar("pace", {
                ...forma.pace,
                mode: evento.target.value as LeagueSettings["pace"]["mode"],
              })
            }
          >
            <option value="async">{t("ligas.criar.ritmoAsync")}</option>
            <option value="live">{t("ligas.criar.ritmoLive")}</option>
          </select>
        </Campo>

        <Campo rotulo={t("ligas.criar.cron")} ajuda={t("ligas.criar.cronAjuda")}>
          <input
            value={forma.pace.cron}
            onChange={(evento) => mudar("pace", { ...forma.pace, cron: evento.target.value })}
            className="num"
          />
        </Campo>

        <Campo rotulo={t("ligas.criar.fuso")}>
          <input
            value={forma.pace.timezone}
            onChange={(evento) => mudar("pace", { ...forma.pace, timezone: evento.target.value })}
            maxLength={64}
          />
        </Campo>

        {aoVivo && (
          <Campo rotulo={t("ligas.criar.esperaAoVivo")}>
            <input
              type="number"
              min={1}
              max={120}
              value={forma.pace.liveTimeoutMinutes}
              onChange={(evento) =>
                mudar("pace", { ...forma.pace, liveTimeoutMinutes: Number(evento.target.value) })
              }
            />
          </Campo>
        )}

        <Campo rotulo={t("ligas.criar.temporadas")}>
          <input
            type="number"
            min={1}
            max={20}
            value={forma.seasons}
            onChange={(evento) => mudar("seasons", Number(evento.target.value))}
          />
        </Campo>

        <Campo rotulo={t("ligas.criar.distribuicao")}>
          <select
            value={forma.clubAssignment}
            onChange={(evento) =>
              mudar("clubAssignment", evento.target.value as LeagueSettings["clubAssignment"])
            }
          >
            <option value="sorteio">{t("ligas.criar.distribuicaoSorteio")}</option>
            <option value="draft">{t("ligas.criar.distribuicaoDraft")}</option>
          </select>
        </Campo>

        <Campo rotulo={t("ligas.criar.dificuldade")}>
          <select
            value={forma.difficulty}
            onChange={(evento) =>
              mudar("difficulty", evento.target.value as LeagueSettings["difficulty"])
            }
          >
            {DIFICULDADES.map((nivel) => (
              <option key={nivel} value={nivel}>
                {t(`ligas.criar.dificuldades.${nivel}`)}
              </option>
            ))}
          </select>
        </Campo>

        <fieldset
          style={{
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            padding: 12,
            display: "grid",
            gap: 10,
          }}
        >
          <legend className="label">{t("ligas.criar.mercado")}</legend>

          <label className="row" style={{ gap: 8 }}>
            <input
              type="checkbox"
              checked={forma.market.humanTrades}
              style={{ minHeight: 0, width: 16, height: 16 }}
              onChange={(evento) =>
                mudar("market", { ...forma.market, humanTrades: evento.target.checked })
              }
            />
            <span style={{ fontSize: 12.5 }}>{t("ligas.criar.mercadoLigado")}</span>
          </label>

          <Campo
            rotulo={t("ligas.criar.razaoMinima")}
            ajuda={t("ligas.criar.razaoMinimaValor", {
              valor: Math.round(forma.market.minOfferRatio * 100),
            })}
          >
            <input
              type="range"
              min={MARKET_MIN_OFFER_RATIO_FLOOR * 100}
              max={100}
              step={5}
              value={Math.round(forma.market.minOfferRatio * 100)}
              disabled={!forma.market.humanTrades}
              onChange={(evento) =>
                mudar("market", {
                  ...forma.market,
                  minOfferRatio: Number(evento.target.value) / 100,
                })
              }
            />
          </Campo>

          <Campo rotulo={t("ligas.criar.negociosPorDupla")}>
            <input
              type="number"
              min={0}
              max={10}
              value={forma.market.maxTradesPerPair}
              disabled={!forma.market.humanTrades}
              onChange={(evento) =>
                mudar("market", { ...forma.market, maxTradesPerPair: Number(evento.target.value) })
              }
            />
          </Campo>

          <label className="row" style={{ gap: 8 }}>
            <input
              type="checkbox"
              checked={forma.market.commissionerApproval}
              disabled={!forma.market.humanTrades}
              style={{ minHeight: 0, width: 16, height: 16 }}
              onChange={(evento) =>
                mudar("market", { ...forma.market, commissionerApproval: evento.target.checked })
              }
            />
            <span style={{ fontSize: 12.5 }}>{t("ligas.criar.aprovacao")}</span>
          </label>
        </fieldset>


        <button
          className="primary"
          disabled={ocupado || forma.name.trim().length < LEAGUE_NAME_MIN_LENGTH}
          onClick={() => void criar()}
        >
          {ocupado ? t("ligas.criar.criando") : t("ligas.criar.criar")}
        </button>
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Saguão
// ---------------------------------------------------------------------------

function Saguao({
  leagueId,
  aoAbrirLiga,
  aoVoltar,
}: {
  leagueId: string;
  aoAbrirLiga: (leagueId: string) => void;
  aoVoltar: () => void;
}): ReactNode {
  const t = useT();
  const sessao = useSessao();
  const [liga, definirLiga] = useState<DetalheDeLiga | null>(null);
  const [erro, definirErro] = useState<string | null>(null);
  useErroEmToast(erro);
  const [aviso, definirAviso] = useState<string | null>(null);
  const [ocupado, definirOcupado] = useState(false);
  const agora = useRelogio(liga?.deadline !== null && liga?.deadline !== undefined);

  const canal = useMemo(() => channelName("league", leagueId), [leagueId]);

  const carregar = useCallback(async () => {
    try {
      definirLiga(
        await sessao.cliente.pedir(
          { metodo: "GET", caminho: `/v1/leagues/${leagueId}` },
          DetalheDeLigaSchema,
        ),
      );
      definirErro(null);
    } catch (causa) {
      definirErro(mensagemDoErro(causa));
    }
  }, [sessao, leagueId]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  /*
   * O socket avisa que algo mudou; quem diz o que mudou é a API. Montar o
   * estado do saguão a partir dos eventos soltos daria duas verdades sobre a
   * mesma liga, e a errada seria justamente a que a tela mostra.
   */
  useEffect(() => {
    sessao.socket.assinar(canal);
    const parar = sessao.socket.ouvir((tipo, _carga, deQualCanal) => {
      if (deQualCanal !== canal) return;
      if (tipo.startsWith("league.")) void carregar();
    });
    return () => {
      parar();
      sessao.socket.desassinar(canal);
    };
  }, [sessao, canal, carregar]);

  const executar = async (acao: () => Promise<string | null>): Promise<void> => {
    definirErro(null);
    definirAviso(null);
    definirOcupado(true);
    try {
      definirAviso(await acao());
    } catch (causa) {
      definirErro(mensagemDoErro(causa));
    } finally {
      definirOcupado(false);
    }
  };

  if (!liga) {
    return (
      <Pagina titulo={t("ligas.titulo")} aoVoltar={aoVoltar}>
        <p className="muted" style={{ textAlign: "center" }}>
          {erro ?? t("online.carregando")}
        </p>
      </Pagina>
    );
  }

  const souComissario = liga.ownerId === sessao.conta?.usuario.id;
  const noSaguao = liga.state === "lobby";
  const ativos = liga.members.filter((membro) => membro.status === "active");

  return (
    <Pagina titulo={liga.name} subtitulo={t(`ligas.estados.${liga.state}`)} aoVoltar={aoVoltar}>
      {aviso && (
        <p className="label" style={{ margin: 0 }}>
          {aviso}
        </p>
      )}

      <Panel title={t("ligas.saguao.configuracao")}>
        <div
          className="grid"
          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 12 }}
        >
          <Stat label={t("ligas.criar.visibilidade")}>
            {t(`ligas.visibilidades.${liga.visibility}`)}
          </Stat>
          <Stat label={t("ligas.criar.ritmo")}>
            {liga.settings.pace.mode === "live"
              ? t("ligas.criar.ritmoLive")
              : t("ligas.criar.ritmoAsync")}
          </Stat>
          <Stat label={t("ligas.saguao.prazo")}>
            {liga.deadline
              ? contagemRegressiva(Date.parse(liga.deadline) - agora)
              : t("ligas.linha.semPrazo")}
          </Stat>
        </div>
        <p className="muted" style={{ margin: "10px 0 0", fontSize: 11.5 }}>
          {t("ligas.saguao.resumo", {
            paises: liga.settings.countries
              .map((codigo) => nomeDoPais(codigo, t))
              .join(", "),
            divisoes: liga.settings.divisionsPerCountry,
            temporadas: liga.settings.seasons,
            dificuldade: t(`ligas.criar.dificuldades.${liga.settings.difficulty}`),
          })}
        </p>
        <p className="muted" style={{ margin: "4px 0 0", fontSize: 11.5 }}>
          {liga.settings.market.humanTrades
            ? t("ligas.saguao.mercadoLigado", {
                razao: Math.round(liga.settings.market.minOfferRatio * 100),
              })
            : t("ligas.saguao.mercadoDesligado")}
        </p>
      </Panel>

      {/*
        O convite de aviso, e só aqui.

        Este é o momento em que o motivo é óbvio: a pessoa acabou de entrar numa
        liga que roda sozinha no horário combinado. Pedir a permissão na tela de
        abertura seria pedir antes de existir motivo, e navegador nenhum dá
        segunda chance depois de um "não".
      */}
      {liga.settings.pace.mode === "async" &&
        ativos.some((membro) => membro.userId === sessao.conta?.usuario.id) && (
          <ConviteDeAvisos />
        )}

      {liga.inviteCode && (
        <Panel title={t("ligas.saguao.convite")}>
          <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
            <span
              className="num"
              style={{ fontSize: 20, fontWeight: 800, letterSpacing: "0.16em", flex: 1 }}
            >
              {liga.inviteCode}
            </span>
            <button
              className="ghost"
              onClick={() =>
                void executar(async () => {
                  await navigator.clipboard.writeText(liga.inviteCode ?? "");
                  return t("ligas.saguao.copiado");
                })
              }
            >
              {t("ligas.saguao.copiar")}
            </button>
            {souComissario && noSaguao && (
              <button
                className="ghost"
                disabled={ocupado}
                onClick={() =>
                  void executar(async () => {
                    const convite = await sessao.cliente.pedir(
                      { metodo: "POST", caminho: `/v1/leagues/${leagueId}/invites`, corpo: {} },
                      ConviteSchema,
                    );
                    await carregar();
                    return convite.expiresAt
                      ? t("ligas.saguao.conviteExpira", { data: dataEHora(convite.expiresAt) })
                      : null;
                  })
                }
              >
                {t("ligas.saguao.gerarConvite")}
              </button>
            )}
          </div>
        </Panel>
      )}

      <Panel
        title={t("ligas.saguao.membros", { total: ativos.length, max: liga.maxHumans })}
        action={
          <EstadoDaConexao />
        }
      >
        <div className="grid" style={{ gap: 8 }}>
          {liga.members.map((membro) => (
            <LinhaDeMembro
              key={membro.userId}
              membro={membro}
              souComissario={souComissario}
              souEu={membro.userId === sessao.conta?.usuario.id}
              ocupado={ocupado}
              aoExpulsar={() =>
                void executar(async () => {
                  await sessao.cliente.pedirVazio({
                    metodo: "POST",
                    caminho: `/v1/leagues/${leagueId}/kick`,
                    corpo: { userId: membro.userId },
                  });
                  await carregar();
                  return null;
                })
              }
            />
          ))}
        </div>
      </Panel>

      <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
        {souComissario && noSaguao && (
          <button
            className="primary"
            disabled={ocupado || ativos.length === 0}
            onClick={() =>
              void executar(async () => {
                definirLiga(
                  await sessao.cliente.pedir(
                    { metodo: "POST", caminho: `/v1/leagues/${leagueId}/start` },
                    DetalheDeLigaSchema,
                  ),
                );
                return null;
              })
            }
          >
            {ocupado ? t("ligas.saguao.comecando") : t("ligas.saguao.comecar")}
          </button>
        )}

        {/* Fora do saguão o mundo já existe, e com ele a visão filtrada. Liga
            pausada ou encerrada continua tendo tabela, elenco e chat para ler. */}
        {!noSaguao && (
          <button className="primary" onClick={() => aoAbrirLiga(leagueId)}>
            {t("ligas.saguao.abrirHub")}
          </button>
        )}

        {souComissario && liga.state === "running" && (
          <>
            <button
              className="ghost"
              disabled={ocupado}
              onClick={() =>
                void executar(async () => {
                  definirLiga(
                    await sessao.cliente.pedir(
                      { metodo: "POST", caminho: `/v1/leagues/${leagueId}/pause`, corpo: {} },
                      DetalheDeLigaSchema,
                    ),
                  );
                  return null;
                })
              }
            >
              {t("ligas.saguao.pausar")}
            </button>
            <button
              className="ghost"
              disabled={ocupado}
              onClick={() =>
                void executar(async () => {
                  const pedido = await sessao.cliente.pedir(
                    {
                      metodo: "POST",
                      caminho: `/v1/leagues/${leagueId}/force-round`,
                      corpo: { expectedRound: liga.round },
                    },
                    RodadaForcadaSchema,
                  );
                  return t("ligas.saguao.rodadaPedida", { rodada: pedido.round });
                })
              }
            >
              {t("ligas.saguao.forcarRodada")}
            </button>
          </>
        )}

        {souComissario && liga.state === "paused" && (
          <button
            className="ghost"
            disabled={ocupado}
            onClick={() =>
              void executar(async () => {
                definirLiga(
                  await sessao.cliente.pedir(
                    { metodo: "POST", caminho: `/v1/leagues/${leagueId}/resume` },
                    DetalheDeLigaSchema,
                  ),
                );
                return null;
              })
            }
          >
            {t("ligas.saguao.retomar")}
          </button>
        )}

        <button
          className="ghost"
          disabled={ocupado}
          style={{ color: "var(--danger)", marginLeft: "auto" }}
          onClick={() =>
            void executar(async () => {
              await sessao.cliente.pedirVazio({
                metodo: "POST",
                caminho: `/v1/leagues/${leagueId}/leave`,
              });
              aoVoltar();
              return null;
            })
          }
        >
          {t("ligas.saguao.sair")}
        </button>
      </div>

      {souComissario && noSaguao && (
        <p className="muted" style={{ margin: 0, fontSize: 11.5 }}>
          {t("ligas.saguao.comecarAjuda")}
        </p>
      )}
    </Pagina>
  );
}

/**
 * Convite para ligar os avisos no aparelho.
 *
 * Um cartão em português antes de qualquer coisa do navegador aparecer. A
 * caixinha do sistema só sobe depois do clique em "quero ser avisado", e é essa
 * a diferença entre uma pessoa que entende o que está aceitando e uma que
 * fecha o susto no automático. O "não" do navegador é definitivo, e este
 * pré-convite é a única chance de explicar antes.
 *
 * Some assim que a decisão é tomada, para nunca virar um cartão que fica
 * pedindo a mesma coisa toda vez que alguém abre a liga.
 */
function ConviteDeAvisos(): ReactNode {
  const t = useT();
  const sessao = useSessao();
  const [visivel, definirVisivel] = useState(() => valeConvidar());
  const [pedindo, definirPedindo] = useState(false);
  const [recado, definirRecado] = useState<string | null>(null);

  if (!visivel) {
    return recado ? (
      <Panel>
        <p className="label" style={{ margin: 0, lineHeight: 1.6 }}>
          {recado}
        </p>
      </Panel>
    ) : null;
  }

  const aceitar = async (): Promise<void> => {
    definirPedindo(true);
    const resultado = await registrarPush(sessao.cliente, { pedirPermissao: true });
    definirRecado(t(RECADO_DO_REGISTRO[resultado]));
    definirVisivel(false);
    definirPedindo(false);
  };

  return (
    <Panel title={t("notificacoes.convite.titulo")}>
      <p className="muted" style={{ margin: "0 0 10px", fontSize: 12 }}>
        {t("notificacoes.convite.corpo")}
      </p>
      <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
        <button className="primary" disabled={pedindo} onClick={() => void aceitar()}>
          {pedindo ? t("notificacoes.convite.pedindo") : t("notificacoes.convite.sim")}
        </button>
        <button
          className="ghost"
          disabled={pedindo}
          onClick={() => {
            // Marcado como convidado mesmo no "agora não": insistir a cada
            // abertura é o caminho mais curto para a pessoa recusar de vez.
            marcarConvite();
            definirVisivel(false);
          }}
        >
          {t("notificacoes.convite.agoraNao")}
        </button>
      </div>
    </Panel>
  );
}

/** O que dizer depois da resposta do navegador. */
const RECADO_DO_REGISTRO: Record<ResultadoDoRegistro, Chave> = {
  registrada: "notificacoes.ativados",
  negada: "notificacoes.negados",
  indisponivel: "notificacoes.indisponiveis",
  semServidor: "notificacoes.semServidor",
  falhou: "notificacoes.falhou",
  // Não acontece por aqui, porque o convite sempre pede a permissão. Fica
  // mapeado para o caso genérico em vez de virar texto em branco na tela.
  adiada: "notificacoes.falhou",
};

function LinhaDeMembro({
  membro,
  souComissario,
  souEu,
  ocupado,
  aoExpulsar,
}: {
  membro: LeagueMember;
  souComissario: boolean;
  souEu: boolean;
  ocupado: boolean;
  aoExpulsar: () => void;
}): ReactNode {
  const t = useT();

  return (
    <div className="row" style={{ gap: 10 }}>
      <span
        aria-hidden="true"
        style={{
          width: 8,
          height: 8,
          borderRadius: 999,
          flexShrink: 0,
          background: membro.online ? "var(--success)" : "var(--text-dim)",
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontWeight: 600 }}>{membro.displayName}</span>
          {/* O título comprado vem do servidor junto do membro, e só vem para
              quem tem o direito. Aqui ele é só um enfeite ao lado do nome. */}
          {membro.cosmetics.title && <TituloDeTreinador params={membro.cosmetics.title} />}
          {membro.role === "commissioner" && (
            <span className="label">{t("ligas.saguao.comissario")}</span>
          )}
          {membro.status !== "active" && (
            <span className="label-muted">{t(`ligas.saguao.situacoes.${membro.status}`)}</span>
          )}
        </div>
        <div className="muted" style={{ fontSize: 11.5 }}>
          {membro.clubName ?? t("ligas.saguao.semClube")}
          {" · "}
          <SituacaoDeDecisao pronto={membro.ready} />
          {membro.lastActionAt &&
            ` · ${t("ligas.saguao.ultimaAcao", {
              quando: tempoRelativo(Date.parse(membro.lastActionAt)),
            })}`}
        </div>
      </div>
      {souComissario && !souEu && membro.status === "active" && (
        <button
          className="ghost"
          disabled={ocupado}
          onClick={aoExpulsar}
          style={{ minHeight: 26, padding: "2px 8px", fontSize: 11, color: "var(--danger)" }}
        >
          {t("ligas.saguao.expulsar")}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Peças de tela
// ---------------------------------------------------------------------------

/** O nome do país, quando ele é um dos que o servidor conhece. */
function nomeDoPais(codigo: string, t: (chave: Chave) => string): string {
  const conhecido = PAISES.find((disponivel) => disponivel === codigo);
  return conhecido ? t(`ligas.criar.paisesNomes.${conhecido}`) : codigo;
}


function Campo({
  rotulo,
  ajuda,
  children,
}: {
  rotulo: string;
  ajuda?: string | undefined;
  children: ReactNode;
}): ReactNode {
  return (
    <label style={{ display: "grid", gap: 4 }}>
      <span className="label-muted">{rotulo}</span>
      {children}
      {ajuda && (
        <span className="muted" style={{ fontSize: 11 }}>
          {ajuda}
        </span>
      )}
    </label>
  );
}
